import React, { useEffect } from 'react';
import NewsletterSignup from '../components/NewsletterSignup';

const UPCOMING = [
  {
    month: 'Sep',
    day: '04',
    title: 'Recruiting timelines for grades 8–10',
    meta: '45 min · veteran competition parents welcome',
    description:
      'When coaches actually start watching, what a realistic outreach calendar looks like by grade, and the two windows most families miss. Led by a parent who took a robotics kid through the full recruiting cycle.',
  },
  {
    month: 'Sep',
    day: '18',
    title: 'Ask a former admissions reader anything',
    meta: '60 min · parents welcome',
    description:
      'An open Q&A with a former admissions reader: how activity lists are actually skimmed, what "demonstrated interest" means in practice, and which parts of the application parents overweight. Bring your own questions.',
  },
  {
    month: 'Oct',
    day: '02',
    title: 'Getting a middle schooler into real research',
    meta: '45 min · with a published researcher',
    description:
      'How to find labs and mentors that take younger students seriously, what a cold email that gets answered looks like, and what a realistic first project is for a 12–14 year old. With a published researcher who started in 8th grade.',
  },
];

const RECORDINGS = [
  {
    date: 'Aug 2026',
    title: 'The summer-program arms race, audited',
    meta: '52 min recording',
    blurb:
      'Which paid summer programs verified mentors say mattered, which were expensive filler, and the free alternatives that showed up in admits’ profiles.',
  },
  {
    date: 'Jul 2026',
    title: 'USACO from Bronze to Platinum: a parent’s map',
    meta: '48 min recording',
    blurb:
      'Training hours by division, when a coach helps versus hurts, and how Platinum finalists actually structured their weeks during the school year.',
  },
  {
    date: 'Jun 2026',
    title: 'Reading your first rejection: a post-mortem clinic',
    meta: '61 min recording',
    blurb:
      'Three anonymized application grids, reviewed line by line with a former admissions reader — what was fixable, what wasn’t, and what transferred to the next cycle.',
  },
  {
    date: 'May 2026',
    title: 'What robotics actually costs, year by year',
    meta: '44 min recording',
    blurb:
      'The six-year cost curves from our parent dataset, presented by two of the families behind the records — including the hindsight chapter.',
  },
  {
    date: 'Apr 2026',
    title: 'Science fair to published paper: the honest path',
    meta: '50 min recording',
    blurb:
      'What separates an ISEF project from a publishable one, how long review actually takes, and why most high-school “publications” don’t count for much.',
  },
];

const WebinarLibraryPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSubscribe = () => {
    document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Webinar library · free
          </span>
          <h1 style={{ maxWidth: '24ch' }}>Live sessions with the people who did it.</h1>
          <p style={{ color: 'var(--ink-secondary)', marginTop: 16, maxWidth: '62ch' }}>
            Every webinar is free to attend and led by a verified mentor or veteran parent — no
            sales pitch, real questions welcome. RSVP by subscribing below; we send the join link
            and the recording to the same list.
          </p>
        </div>
      </header>

      {/* Upcoming */}
      <section className="section warm" style={{ paddingTop: 48 }}>
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">Upcoming</span>
            <h2>On the calendar.</h2>
            <p>
              Seats are capped so the Q&A stays useful. Subscribing reserves your invite for every
              session.
            </p>
          </div>
          <div className="home-webinars-list">
            {UPCOMING.map((w) => (
              <div key={w.title} className="home-webinar-row">
                <div className="home-webinar-date">
                  <div className="home-webinar-month">{w.month}</div>
                  <div className="home-webinar-day">{w.day}</div>
                </div>
                <div className="home-webinar-info">
                  <div className="home-webinar-title">{w.title}</div>
                  <div className="home-webinar-meta">{w.meta}</div>
                  <p className="meta" style={{ marginTop: 8, maxWidth: '68ch' }}>
                    {w.description}
                  </p>
                </div>
                <button className="home-webinar-cta" onClick={scrollToSubscribe} type="button">
                  Save a seat
                </button>
              </div>
            ))}
          </div>
          <p className="meta" style={{ marginTop: 16 }}>
            RSVP works by email — subscribe once and we’ll send the join link for any session you
            want to attend.
          </p>
        </div>
      </section>

      {/* Recordings */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <span className="eyebrow">Recordings</span>
            <h2>Past sessions, in full.</h2>
            <p>
              Recordings go out to subscribers after each session. A few recent ones, so you know
              what to expect.
            </p>
          </div>
          <div className="home-resources-grid">
            {RECORDINGS.map((r) => (
              <div key={r.title} className="card">
                <div className="meta" style={{ marginBottom: 8 }}>
                  {r.date} · {r.meta}
                </div>
                <h3 style={{ marginBottom: 10 }}>{r.title}</h3>
                <p className="meta" style={{ fontSize: 14.5, lineHeight: 1.6 }}>{r.blurb}</p>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 14, alignSelf: 'flex-start' }}
                  onClick={scrollToSubscribe}
                  type="button"
                >
                  Get the recording
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <NewsletterSignup
        variant="band"
        source="webinar-library"
        heading="RSVP once, get every session."
        subtext="Subscribe and we’ll send join links for upcoming webinars and recordings of past ones — free, no spam, unsubscribe anytime."
      />
    </>
  );
};

export default WebinarLibraryPage;
